import { Action, createReducer, on } from '@ngrx/store';
import * as catalogsActions from './catalogs.actions';
import * as ordersActions from '../../orders/store/orders.actions';
import { ICartItem, ICatalog } from '../models/catalogs.models';
import { ILocation } from '../../auth/models/auth-response';
import { IInventoryFilter } from 'src/app/shared/models/products.models';
import { refillInventoryFilter } from 'src/app/shared/helper';

export interface CatalogsState {
  catalogs: ICatalog[];
  allCatalogs: ICatalog[];
  catalog: ICatalog;
  locations: ILocation[];
  loading: boolean;
  metadata: any;
  cart: ICartItem[];
  productFilterState: ProductsFilterState;
}

export interface ProductsFilterState {
  advanced: IInventoryFilter;
}

export const initialProductsState: CatalogsState = {
  catalogs: [],
  allCatalogs: [],
  catalog: {} as ICatalog,
  locations: [],
  loading: false,
  metadata: null,
  cart: [],
  productFilterState: {
    advanced: refillInventoryFilter()
  },
};

const featureReducer = createReducer(
  initialProductsState,
  on(catalogsActions.Loading, (state, action) => ({ ...state, loading: action.loading })),
  on(catalogsActions.LoadCatalogsSuccess, (state, action) => ({
    ...state,
    allCatalogs: [...action.resp.data],
    metadata: action.resp.metadata,
  })),
  on(catalogsActions.AppendCatalogsSuccess, (state, action) => ({
    ...state,
    allCatalogs: state.allCatalogs.concat(action.resp.data),
    metadata: action.resp.metadata,
  })),
  on(catalogsActions.LoadProductsSuccess, (state, action) => ({
    ...state,
    catalog: { ...action.resp.object },
    metadata: action.resp.metadata,
  })),
  on(catalogsActions.AppendProductsSuccess, (state, action) => ({
    ...state,
    catalog: {
      ...state.catalog,
      products: state.catalog.products.concat(action.resp.object.products),
    },
    metadata: action.resp.metadata,
  })),
  // Cart Actions
  on(catalogsActions.LoadCartSuccess, (state, action) => ({
    ...state,
    cart: action.carts,
  })),
  on(catalogsActions.AddToCartSuccess, (state, action) => {
    // Filter out all cart items with no products
    const filteredCart = state.cart.filter(item => item.products.length > 0);

    // If action.cart is null, it means the cart item was removed
    if (!action.cart) {
      // If there was only one cart left, clear the cart array
      if (filteredCart.length === 1) {
        return {
          ...state,
          cart: [],
        };
      }
      // Otherwise, just return the filtered cart
      return {
        ...state,
        cart: filteredCart,
      };
    }

    // Otherwise, update or add the cart item
    const cartIndex = filteredCart.findIndex(item => item.id === action.cart.id);

    if (cartIndex > -1) {
      // Update existing cart item
      return {
        ...state,
        cart: filteredCart.map((item, idx) =>
          idx === cartIndex ? { ...item, ...action.cart } : item
        ),
      };
    } else {
      // Add new cart item
      return {
        ...state,
        cart: [...filteredCart, action.cart],
      };
    }
  }),
  on(catalogsActions.BatchCartUpdateSuccess, (state, action) => {
    // Filter out all cart items with no products
    const filteredCart = state.cart.filter(item => item.products.length > 0);

    // If action.cart is null, it means the cart item was removed
    if (!action.cart) {
      // If there was only one cart left, clear the cart array
      if (filteredCart.length === 1) {
        return {
          ...state,
          cart: [],
        };
      }
      // Otherwise, just return the filtered cart
      return {
        ...state,
        cart: filteredCart,
      };
    }

    // Otherwise, update or add the cart item
    const cartIndex = filteredCart.findIndex(item => item.id === action.cart.id);

    if (cartIndex > -1) {
      // Update existing cart item
      return {
        ...state,
        cart: filteredCart.map((item, idx) =>
          idx === cartIndex ? { ...item, ...action.cart } : item
        ),
      };
    } else {
      // Add new cart item
      return {
        ...state,
        cart: [...filteredCart, action.cart],
      };
    }
  }),
  on(ordersActions.SendOrderSuccess, ordersActions.SendRFQSuccess, (state, action) => {
    const { order } = action;

    const newCart = state.cart.filter((item) => {
      return item.id !== order.id;
    });

    return {
      ...state,
      cart: [...newCart],
    };
  }),
  on(catalogsActions.UpdateCartSuccess, (state, action) => {
    const { cart } = action;

    const updatedCart = state.cart.map((item) =>
      item.id === cart.id ? { ...item, ...cart } : item
    );

    return {
      ...state,
      cart: updatedCart,
    };
  }),
  on(catalogsActions.DeleteCartSuccess, (state, action) => {
    const { cart } = action;

    const newCart = state.cart.filter((item) => {
      return item.id !== cart.id;
    });

    return {
      ...state,
      cart: [...newCart],
    };
  }),
  // Old Actions
  on(catalogsActions.FetchAllCatalogsSuccess, (state, action) => ({
    ...state,
    allCatalogs: action.catalogs.data,
  })),
  on(catalogsActions.SetSelectedCatalog, (state, action) => ({
    ...state,
    catalog: action.catalog as ICatalog,
  })),
  on(catalogsActions.FetchCatalogByIdSuccess, (state, action) => ({
    ...state,
    catalog: action.catalog,
  })),
  on(catalogsActions.EmptyCatalog, (state) => ({
    ...state,
    catalog: {} as ICatalog,
  })),
  on(catalogsActions.EmptyCatalogs, (state) => ({
    ...state,
    catalogs: [],
  })),
  on(catalogsActions.GetLocationsByCompanyIdSuccess, (state, action) => ({
    ...state,
    locations: action.locations.data,
  })),
  on(catalogsActions.SetInventoryFilter, (state, action) => ({
    ...state,
    productFilterState: {
      advanced: action.advanced!
    }
  })),
  // on(catalogsActions.SetOrderSearch, (state, action) => ({
  //   ...state,
  //   productFilterState: {
  //     ...state.productFilterState,
  //     keyword : action.keyword
  //   }
  // }))
);

export function catalogsReducer(state: CatalogsState | undefined, action: Action) {
  return featureReducer(state, action);
}
